const configuredApiUrl =
  import.meta.env.VITE_SAILPLOT_API_URL?.trim() || "https://api.sailplot.app";

export const sailplotApiUrl = (() => {
  const url = new URL(configuredApiUrl);
  if (!["http:", "https:"].includes(url.protocol)) {
    throw new Error(`Invalid SailPlot API URL: ${configuredApiUrl}`);
  }
  return url.origin;
})();

interface ApiErrorBody {
  message?: string;
}

async function postJson<ResponseBody>(
  path: string,
  body: Record<string, unknown>,
  headers?: Record<string, string>,
): Promise<ResponseBody> {
  const response = await fetch(`${sailplotApiUrl}${path}`, {
    method: "POST",
    headers: { "content-type": "application/json", ...headers },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = `Request failed (${response.status}).`;
    try {
      const payload = (await response.json()) as ApiErrorBody;
      if (payload.message) message = payload.message;
    } catch {
      // Keep the safe, generic error if the API did not return JSON.
    }
    throw new Error(message);
  }

  return (await response.json()) as ResponseBody;
}

async function getJson<ResponseBody>(path: string): Promise<ResponseBody> {
  const response = await fetch(`${sailplotApiUrl}${path}`);
  if (!response.ok) {
    throw new Error(`Request failed (${response.status}).`);
  }
  return (await response.json()) as ResponseBody;
}

export interface CheckoutOrder {
  success: true;
  orderId: string;
  checkoutToken: string;
  status: "PENDING_PAYMENT";
}

export const createCheckoutOrder = (
  planId: string,
  checkoutId: string,
  context: {
    draftId: string | null;
    language: "de" | "en";
    legalConsent: {
      termsVersion: string;
      refundPolicyVersion: string;
      privacyPolicyVersion: string;
      withdrawalNoticeVersion: string;
      accepted: true;
      immediatePerformanceRequested: boolean;
      immediatePerformanceAcknowledged: boolean;
    };
  },
) =>
  postJson<CheckoutOrder>("/v1/orders", {
    planId,
    checkoutId,
    language: context.language,
    legalConsent: context.legalConsent,
    ...(context.draftId ? { draftId: context.draftId } : {}),
  });

export interface ContractActionReceipt {
  success: true;
  referenceId: string;
  receivedAt: string;
  declaration: Record<string, unknown>;
}

export const submitContractAction = (
  input: {
    action: "cancel" | "withdraw";
    cancellationType?: "ordinary" | "extraordinary";
    name: string;
    email: string;
    contractReference: string;
    requestedEffectiveAt?: string;
    reason?: string;
    language: "de" | "en";
    turnstileToken: string;
    confirmation: true;
  },
  idempotencyKey: string,
) =>
  postJson<ContractActionReceipt>(
    "/v1/contract-actions",
    { ...input },
    { "Idempotency-Key": idempotencyKey },
  );

export interface ImprovementSubmission {
  success: true;
  improvementId: string;
  status: "PENDING_REVIEW";
  trackingPath: string;
}

export const submitImprovement = (input: {
  name: string;
  email: string;
  publishName: boolean;
  language: "de" | "en";
  includeCurrentPlot: boolean;
  currentPlotUrl?: string;
  publicName?: string;
  title: string;
  description: string;
  turnstileToken: string;
  context: { source: string; version: string; currentUrl: string };
}) => postJson<ImprovementSubmission>("/v1/improvement", input);

export interface ImprovementStatus {
  success: true;
  improvementId: string;
  status:
    | "PENDING_REVIEW"
    | "APPROVED"
    | "CREATING_ISSUE"
    | "ISSUE_CREATED"
    | "REJECTED";
  issueUrl: string | null;
}

export const getImprovementStatus = (improvementId: string) =>
  getJson<ImprovementStatus>(
    `/v1/improvement/${encodeURIComponent(improvementId)}`,
  );
